import React, { useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import { captureSession, isSessionCurrent } from '../../utils/authSession';

const COUNT_LABELS = {
  activeClinicLinks: 'Active customer links',
  sourceManagedClinicLinks: 'Mirrored client-clinic links',
  masterAccounts: 'Portal logins retained',
  clinicAccounts: 'Customer logins',
  orders: 'Distributor orders',
  ivrs: 'Distributor IVRs',
  shipments: 'Distributor shipments',
  bankEntries: 'Bank entries retained',
};

export default function DistributorCustomerSeparation({ distributor, admin }) {
  const { busy, setBusy, setError, setNotice, load } = admin;
  const [review, setReview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const generation = useRef(0);
  const pending = useRef(false);
  const reviewSession = useRef(null);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    []
  );

  const separate = async (apply) => {
    if (pending.current || busy || (apply && (!review || review.blocked || !confirmed))) return;
    if (apply && !isSessionCurrent(reviewSession.current)) {
      setReview(null);
      setConfirmed(false);
      setError('Your session changed. Review the customer separation again before continuing.');
      return;
    }
    pending.current = true;
    const request = ++generation.current;
    const session = captureSession();
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = (
        await distributorService.separateDistributorCustomers(
          distributor._id,
          apply ? { apply: true, expectedState: review.expectedState } : { apply: false }
        )
      )?.data?.data;
      if (request !== generation.current || !isSessionCurrent(session)) return;
      if (
        !result ||
        typeof result.blocked !== 'boolean' ||
        typeof result.independentCustomers !== 'boolean' ||
        typeof result.expectedState !== 'string' ||
        !/^[a-f\d]{64}$/.test(result.expectedState) ||
        Object.keys(COUNT_LABELS).some(
          (key) => !Number.isSafeInteger(result.counts?.[key]) || result.counts[key] < 0
        )
      )
        throw new Error('Invalid separation review');
      if (apply || (result.alreadyIndependent === true && !result.blocked)) {
        if (
          result.blocked ||
          result.independentCustomers !== true ||
          (result.applied !== true && result.alreadyIndependent !== true)
        )
          throw new Error('Unconfirmed separation');
        setReview(null);
        setConfirmed(false);
        await load();
        if (request !== generation.current || !isSessionCurrent(session)) return;
        setNotice(
          'Customer lists separated. Portal logins and bank balances were retained. Direct MedEffects records were not changed.'
        );
      } else {
        setReview(result);
        reviewSession.current = session;
        setConfirmed(false);
      }
    } catch {
      if (request !== generation.current || !isSessionCurrent(session)) return;
      setReview(null);
      setConfirmed(false);
      setError(
        apply
          ? 'Customer separation could not be confirmed. Refresh the distributor list and run a new review before trying again.'
          : 'Customer separation review is unavailable. No changes were requested. Please try again.'
      );
    } finally {
      pending.current = false;
      // The parent owns this shared lock; conversion may unmount this panel during list refresh.
      setBusy(false);
    }
  };

  return (
    <section className="me-distributor-admin-card space-y-4" aria-label="Separate customer lists">
      <h2 className="text-lg font-semibold text-slate-900">Separate customer lists</h2>
      <p className="text-sm text-slate-600">
        Review {distributor.name} before removing mirrored client-clinic links. This keeps its
        portal logins and product bank, leaves direct MedEffects records unchanged, and starts an
        independent customer list. A review does not save changes.
      </p>
      <button
        type="button"
        disabled={busy}
        className="me-distributor-admin-secondary"
        onClick={() => separate(false)}
      >
        Review separation
      </button>
      {review && (
        <div
          className="me-distributor-admin-selection space-y-3 text-sm"
          role="region"
          aria-label="Customer separation review"
        >
          <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {Object.entries(COUNT_LABELS).map(([key, label]) => (
              <div key={key}>
                <dt>{label}</dt>
                <dd className="font-semibold">{review.counts[key]}</dd>
              </div>
            ))}
          </dl>
          {review.blocked ? (
            <p role="alert" className="text-amber-800">
              Separation is blocked. Existing distributor records, customer logins, or manually
              linked customers need administrator review. Nothing has been changed.
            </p>
          ) : (
            <>
              <p>
                Mirrored links will be deactivated. Direct clinics will not be deleted or copied.
              </p>
              <label className="me-distributor-admin-confirm flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={confirmed}
                  disabled={busy}
                  onChange={(event) => setConfirmed(event.target.checked)}
                />
                I reviewed the counts and want a separate customer list for {distributor.name}
              </label>
              <button
                type="button"
                disabled={busy || !confirmed}
                className="me-distributor-admin-primary"
                onClick={() => separate(true)}
              >
                Separate customer lists
              </button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
